/**
 * Extracts a clean filename from a citation URL by removing repository/section prefixes and page parameters
 * @param fileUrl The citation URL
 * @param defaultName The default name to use if extraction fails
 * @returns A clean filename without repository/section prefixes or page parameters
 */
export function extractCleanFilename(fileUrl: string, defaultName = "document"): string {
  if (!fileUrl) {
    return defaultName
  }

  // First decode the entire URL to handle any encoded characters
  let decodedUrl = decodeURIComponent(fileUrl)

  // Strip #page parameter and anything after it (e.g., file.pdf#page=5 becomes file.pdf)
  if (decodedUrl.includes("#page=")) {
    decodedUrl = decodedUrl.split("#page=")[0]
  }

  // Get the last part of the URL path
  let filename = decodedUrl.split("/").pop() || defaultName

  // Always take the part after the last colon (library/section prefix)
  // This handles any library name prefix like "xone-spo-sop:" without special casing
  if (filename.includes(":")) {
    const parts = filename.split(":")
    filename = parts[parts.length - 1] || filename
  }

  return filename
}

/**
 * Gets the file extension from a filename
 * @param filename The filename to extract the extension from
 * @returns The file extension (without the dot) or an empty string if no extension is found
 */
export function getFileExtension(filename: string): string {
  if (!filename) {
    return ""
  }

  const parts = filename.split(".")
  return parts.length > 1 ? parts.pop() || "" : ""
}
